/* sugar:assets/sugar-animated-icon.js@2.2.9 */
/* <sugar-animated-icon src="…"> — inline-SVG renderer for the icon CDN's
 * inline families (lmd-*, lottie-*, sugar-*). SMIL animations only run when the
 * SVG is inline in the DOM: as an <img> or CSS mask the browser freezes them at
 * t=0, and draw-in icons are blank at t=0 (validated 2026-08-20, Chrome).
 * So this element fetches the SVG text (CDN serves ACAO: *) and injects it.
 * Inline also means currentColor recoloring works, same as the mask family.
 * Fetch failure hides the element — parity with the mask snippet's fallback.
 * SMIL ignores prefers-reduced-motion, so when the visitor asks for less motion
 * the injected drawing is paused and jumped to its end state (a looping icon
 * shows one still frame).
 * SMIL runs on the main thread: while a drawing plays, the page renders a frame
 * on every frame, and each of those re-styles every CSS animation on the page.
 * So a drawing only plays while it is near the screen; off screen, or inside
 * something closed, it holds still.
 */

const cache = new Map(); // url -> Promise<string>
const reduceMotion = window.matchMedia ? window.matchMedia("(prefers-reduced-motion: reduce)") : null;

function settle(svg) {
  if (!svg || !reduceMotion || !reduceMotion.matches || !svg.pauseAnimations) return;
  svg.pauseAnimations();
  svg.setCurrentTime(60); // past any draw-in; fill="freeze" holds the final frame
}

function hold(host) {
  const svg = host.querySelector("svg");
  if (!svg || !svg.pauseAnimations || (reduceMotion && reduceMotion.matches)) return;
  if (host._near === false) svg.pauseAnimations();
  else svg.unpauseAnimations();
}

const nearScreen =
  "IntersectionObserver" in window
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            entry.target._near = entry.isIntersecting;
            hold(entry.target);
          }
        },
        { rootMargin: "100px 0px" }
      )
    : null;

function fetchSvg(url) {
  let p = cache.get(url);
  if (!p) {
    p = fetch(url).then((r) => {
      if (!r.ok) throw new Error(`icon ${r.status}`);
      return r.text();
    });
    p.catch(() => cache.delete(url)); // don't cache failures
    cache.set(url, p);
  }
  return p;
}

class SugarAnimatedIcon extends HTMLElement {
  async connectedCallback() {
    if (nearScreen) nearScreen.observe(this);
    const src = this.getAttribute("src");
    if (!src || this._loaded === src) return;
    try {
      const text = await fetchSvg(src);
      if (!this.isConnected || this.getAttribute("src") !== src) return;
      this.innerHTML = text; // fresh subtree per instance restarts SMIL
      this._loaded = src;
      settle(this.querySelector("svg"));
      hold(this);
    } catch {
      this.style.display = "none";
    }
  }

  disconnectedCallback() {
    if (nearScreen) nearScreen.unobserve(this);
  }

  /* Re-inject to replay the animation from t=0. */
  replay() {
    this._loaded = null;
    this.connectedCallback();
  }
}

if (!customElements.get("sugar-animated-icon")) {
  customElements.define("sugar-animated-icon", SugarAnimatedIcon);
}
